<template>
  <div class="aia-card">
    <div class="aia-header">
      <div class="aia-header-left">
        <h4 class="aia-title">{{ normalizedAction }}</h4>
        <p class="aia-date">{{ formatDate(action.created_at) }}</p>
      </div>
      <div class="aia-header-right">
        <span class="aia-agent-badge">
          <svgicon name="icon-ai-agent-sparkle" width="10" height="13" />
          {{ action.agent_name }}
          <span class="aia-beta-tag">Beta</span>
        </span>
      </div>
    </div>

    <div class="aia-body">
      <div class="aia-details-header" @click="toggleDetailsExpanded">
        <svgicon name="icon-action-details" width="10" height="10" />
        <span class="aia-details-title">ACTION DETAILS</span>
        <svgicon
          :name="isDetailsExpanded ? 'arrow-up' : 'arrow-down'"
          class="aia-toggle-icon"
          width="12"
          height="12"
        />
      </div>

      <div v-if="isDetailsExpanded" class="aia-details-content">
        <div class="aia-details-grid">
          <div class="aia-detail-row">
            <div class="aia-detail-item">
              <label>INPUT</label>
              <p class="aia-io-text">{{ action.input && action.input.userText || 'NA' }}</p>
            </div>
            <div class="aia-detail-item aia-detail-item--right">
              <label>OUTPUT</label>
              <p class="aia-io-text">{{ action.input && action.input.systemText || 'NA' }}</p>
            </div>
          </div>

          <div class="aia-detail-row">
            <div class="aia-detail-item">
              <label>SENDER EMAIL</label>
              <p>{{ action.sender_email }}</p>
            </div>
            <div class="aia-detail-item aia-detail-item--right">
              <label>RECEIVER EMAIL</label>
              <p>{{ action.receiver_email }}</p>
            </div>
          </div>

          <div class="aia-detail-row">
            <div class="aia-detail-item" v-if="action.approved_by">
              <label>{{ isInvoice ? 'APPROVED BY' : 'ACCEPTED BY' }}</label>
              <p>{{ action.approved_by }}</p>
            </div>
            <div class="aia-detail-item">
              <label>STATUS</label>
              <p :class="getStatusClass(action.status)">
                {{ action.status }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="hasEmailAttachment" class="aia-email-section">
          <div class="aia-email-info">
            <svgicon name="icon-email-transporter" width="32" height="32" />
            <div class="aia-email-text">
              <strong>Original Email</strong>
              <p>View original email message</p>
            </div>
          </div>
          <button
            class="aia-download-btn"
            :disabled="isDownloading"
            @click="downloadEmail"
          >
            {{ isDownloading ? 'LOADING...' : 'DOWNLOAD' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIAgentActionCard',
  props: {
    action: {
      type: Object,
      required: true,
    },
    isInvoice: {
      type: Boolean,
      default: false,
    },
    timeFormat: {
      type: String,
      default: 'DD MMM YYYY, hh:mm A',
    },
  },
  data() {
    return {
      isDetailsExpanded: true,
      isDownloading: false,
    };
  },
  computed: {
    normalizedAction() {
      return this.action.action
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    hasEmailAttachment() {
      return !!(this.action.email_attachment || (this.action.s3meta && this.action.s3meta.key));
    },
    emailAttachmentUrl() {
      return this.action.email_attachment || null;
    },
  },
  methods: {
    toggleDetailsExpanded() {
      this.isDetailsExpanded = !this.isDetailsExpanded;
    },
    async downloadEmail() {
      if (this.action.email_attachment) {
        window.open(this.action.email_attachment, '_blank');
        return;
      }
      this.isDownloading = true;
      try {
        const params = { actionId: this.action._id };
        let blob;
        if (this.isInvoice) {
          blob = await this.$store.dispatch('paymentsV2/fetchAIAgentActionEmailDownloadUrl', {
            ...params,
            invoiceId: this.action.entity_id,
          });
        } else {
          blob = await this.$store.dispatch('order/fetchAIAgentActionEmailDownloadUrl', {
            ...params,
            orderId: this.action.entity_id,
          });
        }
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `email-${this.action._id}.eml`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        this.$errorMessage({ message: 'Failed to download email' });
      } finally {
        this.isDownloading = false;
      }
    },
    formatDate(date) {
      return this.$moment(date).format("DD MMM 'YY- hh:mm A");
    },
    getStatusClass(status) {
      const statusLower = status.toLowerCase();
      if (statusLower === 'success') {
        return 'aia-status-success';
      }
      if (statusLower === 'failure' || statusLower === 'rejected') {
        return 'aia-status-failure';
      }
      if (statusLower === 'pending' || statusLower === 'in progress') {
        return 'aia-status-pending';
      }
      return '';
    },
  },
};
</script>

<style scoped lang="scss">
.aia-card {
  background: #ffffff;
  border: 1px solid #dfe1e6;
  border-radius: 5px;
  margin-bottom: 10px;
  overflow: hidden;

  .aia-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px 15px;
    background: #fafbfc;

    .aia-header-left {
      flex: 1;

      .aia-title {
        margin: 0 0 4px 0;
        font-size: 12px;
        font-weight: 600;
        line-height: 100%;
        letter-spacing: 0%;
        color: #234357;
      }

      .aia-date {
        margin: 0;
        font-size: 10px;
        font-weight: 400;
        line-height: 12px;
        letter-spacing: 0.5px;
        color: #616161;
      }
    }

    .aia-header-right {
      display: flex;
      align-items: center;
      margin-left: 10px;

      .aia-agent-badge {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 0.5px 2px;
        background: #D5F9D1;
        border-radius: 2px;
        font-size: 10px;
        font-weight: 600;
        color: #041F04;

        .aia-beta-tag {
          padding: 2px 5px;
          background: #ffab00;
          border-radius: 2px;
          font-size: 9px;
          font-weight: 700;
          color: #172b4d;
          text-transform: uppercase;
        }
      }
    }
  }

  .aia-body {
    padding: 0;

    .aia-details-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 15px;
      background: #D9E6FF80;
      cursor: pointer;

      &:hover {
        background: #D9E6FF;
      }

      .aia-details-title {
        flex: 1;
        font-size: 12px;
        font-weight: 600;
        line-height: 100%;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        color: #376F99;
      }

      .aia-toggle-icon {
        color: #0052cc;
      }
    }

    .aia-details-content {
      padding: 15px;
      background: #D9E6FF80;

      .aia-details-grid {
        .aia-detail-row {
          display: flex;
          gap: 20px;
          margin-bottom: 15px;

          &:last-child {
            margin-bottom: 0;
          }

          .aia-detail-item {
            flex: 1;

            &--right {
              text-align: right;

              label {
                text-align: right;
              }
            }

            label {
              display: block;
              margin-bottom: 4px;
              font-size: 10px;
              font-weight: 400;
              color: #737373;
              text-transform: uppercase;
            }

            p {
              margin: 0;
              font-size: 12px;
              font-weight: 600;
              color: #234357;
              word-break: break-word;

              &.aia-io-text {
                font-size: 10px;
                font-weight: 500;
              }

              &.aia-status-success {
                color: #404040;
                background: #D5F9D1;
                font-size: 10px;
                font-weight: 600;
                padding: 2px 8px;
                border-radius: 3px;
                display: inline-block;
              }

              &.aia-status-failure {
                color: #ffffff;
                background: #bf2600;
                font-size: 10px;
                font-weight: 600;
                padding: 2px 8px;
                border-radius: 3px;
                display: inline-block;
              }

              &.aia-status-pending {
                color: #ff8b00;
                font-size: 10px;
                font-weight: 600;
              }
            }
          }
        }
      }

      .aia-email-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 15px;
        padding: 12px 15px;
        background: #f4f5f7;
        border: 1px solid #dfe1e6;
        border-radius: 3px;

        .aia-email-info {
          display: flex;
          align-items: center;
          gap: 10px;

          svg, .svg-icon {
            color: #0052cc;
            fill: currentColor;
          }

          .aia-email-text {
            strong {
              display: block;
              font-size: 12px;
              font-weight: 600;
              color: #172b4d;
              margin-bottom: 2px;
            }

            p {
              margin: 0;
              font-size: 11px;
              color: #6b778c;
            }
          }
        }

        .aia-download-btn {
          padding: 6px 14px;
          background: transparent;
          color: #0076DD;
          border: none;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 700;
          line-height: 14px;
          letter-spacing: 0px;
          text-align: right;
          text-decoration: none;
          text-transform: uppercase;
          cursor: pointer;
          transition: opacity 0.2s;

          &:hover:not(:disabled) {
            opacity: 0.8;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}
</style>
